import type { FamilyRelative } from './FamilyRelative.js';

/** What the graph reads of a person. The link arrays sit on the parent: they are the parent's side of every relation. */
export type FamilyMember = Pick<
  FamilyRelative,
  '_id' | 'sorgeberechtigteKinderIDs' | 'spendenKinderIDs' | 'geburtsDatum'
>;

/** How a card relates to the person the graph is drawn around. */
export type FamilyRelation =
  | 'self'
  | 'sorgeberechtigt'
  | 'spender'
  | 'kind'
  | 'spendenkind'
  | 'geschwister'
  | 'weitereSorgeberechtigte'
  | 'grosselternteil'
  | 'enkelkind'
  /** further away - aunts, cousins, the Spender of a sibling: the lines tell how */
  | 'weitere';

export interface FamilyGraphNode<T extends FamilyMember> {
  member: T;
  relation: FamilyRelation;
  x: number;
  y: number;
}

/** One family unit's connector: an svg path of the bus and the drops into its parents and children. */
export interface FamilyGraphLine {
  path: string;
  spender: boolean;
  touchesSelf: boolean;
}

/**
 * One parent-child link and its route through the unit's bus. Every link enters its child on a port of its own, so the
 * last drop belongs to this link alone - that is where a click or hover tells the links of a shared bus apart.
 */
export interface FamilyGraphLink {
  parentID: string;
  childID: string;
  spender: boolean;
  path: string;
}

export interface FamilyGraph<T extends FamilyMember> {
  width: number;
  height: number;
  nodes: Array<FamilyGraphNode<T>>;
  lines: FamilyGraphLine[];
  links: FamilyGraphLink[];
}

export const FAMILY_NODE_WIDTH = 280;
export const FAMILY_NODE_HEIGHT = 184;
const GAP_X = 32;
const PADDING = 8;
const PORT_SPACING = 20;
const LANE_SPACING = 24;
const MIN_GAP_Y = 88;
/** ponytail: lanes are brute-forced; above this many family units between two generations they keep their order */
const MAX_PERMUTED_BUSES = 6;

interface Link {
  from: string;
  to: string;
  spender: boolean;
}

/** The children sharing exactly the same parents (or the same Spender), drawn as one bus. */
interface Bus {
  spender: boolean;
  parents: string[];
  children: string[];
  /** port per parent */
  parentX: Map<string, number>;
  /** port per link, keyed by `linkKey` - a child with two parents takes two drops */
  childX: Map<string, number>;
  minX: number;
  maxX: number;
}

const linkKey = (parentID: string, childID: string) => `${parentID}>${childID}`;

const average = (values: number[]) => values.reduce((sum, value) => sum + value, 0) / values.length;

const permutations = <T>(items: T[]): T[][] =>
  items.length < 2
    ? [items]
    : items.flatMap((item, i) =>
        permutations([...items.slice(0, i), ...items.slice(i + 1)]).map(rest => [item, ...rest]),
      );

const inside = (x: number, bus: Bus) => x > bus.minX && x < bus.maxX;

/** Crossings of the buses stacked in this order: drops of a lower bus pass the bars above it, and vice versa. */
const crossingCount = (lanes: Bus[]) =>
  lanes.reduce(
    (sum, upper, i) =>
      sum +
      lanes
        .slice(i + 1)
        .reduce(
          (count, lower) =>
            count +
            [...lower.parentX.values()].filter(x => inside(x, upper)).length +
            [...upper.childX.values()].filter(x => inside(x, lower)).length,
          0,
        ),
    0,
  );

/**
 * Lays out the family around `selfID` in rows of generations: everybody in `members` that a chain of Sorgeberechtigten-
 * or Spender-links connects to the person - the server decides how far that reaches. A Spender sits a row above their
 * Spendenkind, like a Sorgeberechtigter.
 *
 * Each group of children sharing exactly the same parents hangs from one bus. Rows are ordered by where their links
 * sit (barycenter), so mother-only, shared and father-only children line up under their parents and the buses do not
 * cross; within a group the oldest comes first.
 */
export function getFamilyGraph<T extends FamilyMember>(selfID: string, members: T[]): FamilyGraph<T> {
  const self = members.find(member => member._id === selfID);
  if (!self) return { width: 0, height: 0, nodes: [], lines: [], links: [] };

  const holds = (parent: T, child: T) =>
    !!parent.sorgeberechtigteKinderIDs?.includes(child._id) || !!parent.spendenKinderIDs?.includes(child._id);
  // breadth first, so everybody sits in the generation of their shortest way to the person
  const generation = new Map<string, number>([[selfID, 0]]);
  const queue: T[] = [self];
  for (const member of queue) {
    const memberGeneration = generation.get(member._id) ?? 0;
    for (const other of members) {
      if (generation.has(other._id)) continue;
      const step = holds(other, member) ? -1 : holds(member, other) ? 1 : 0;
      if (!step) continue;
      generation.set(other._id, memberGeneration + step);
      queue.push(other);
    }
  }

  const links: Link[] = [];
  for (const member of members) {
    const memberGeneration = generation.get(member._id);
    if (memberGeneration === undefined) continue;
    for (const id of member.sorgeberechtigteKinderIDs ?? [])
      if (generation.get(id) === memberGeneration + 1) links.push({ from: member._id, to: id, spender: false });
    for (const id of member.spendenKinderIDs ?? [])
      if (generation.get(id) === memberGeneration + 1) links.push({ from: member._id, to: id, spender: true });
  }
  const linked = (from: string, to: string, spender: boolean) =>
    links.some(link => link.from === from && link.to === to && link.spender === spender);
  const sbParentIDs = (member: T) =>
    links.filter(link => link.to === member._id && !link.spender).map(link => link.from);
  const sbChildIDs = (member: T) =>
    links.filter(link => link.from === member._id && !link.spender).map(link => link.to);
  const linkedChildIDs = (member: T) => links.filter(link => link.from === member._id).map(link => link.to);
  const spenderOnly = (member: T) =>
    links.some(link => link.from === member._id && link.spender) &&
    !links.some(link => link.from === member._id && !link.spender);
  const birth = (member: T) => member.geburtsDatum?.getTime() ?? Infinity;
  const tieBreak = (a: T, b: T) => Number(spenderOnly(a)) - Number(spenderOnly(b)) || birth(a) - birth(b) || 0;

  const rows = [...new Set(generation.values())]
    .sort((a, b) => a - b)
    .map(rowGeneration => members.filter(member => generation.get(member._id) === rowGeneration).sort(tieBreak));

  const index = new Map<string, number>();
  const reindex = () =>
    rows.forEach(row => row.forEach((member, i) => index.set(member._id, i - (row.length - 1) / 2)));
  // a card without links into the other row keeps its place
  const barycenter = (member: T, ids: string[]) =>
    ids.length ? average(ids.map(id => index.get(id) ?? 0)) : (index.get(member._id) ?? 0);
  const sortRow = (row: T[], linkedIDs: (member: T) => string[]) =>
    row.sort((a, b) => barycenter(a, linkedIDs(a)) - barycenter(b, linkedIDs(b)) || tieBreak(a, b));
  reindex();
  for (const pass of ['down', 'up', 'down'] as const) {
    const ordered = pass === 'down' ? rows : [...rows].reverse();
    ordered.forEach((row, i) => {
      if (i === 0) return;
      sortRow(row, pass === 'down' ? sbParentIDs : linkedChildIDs);
      reindex();
    });
  }

  const width = Math.max(...rows.map(row => row.length * FAMILY_NODE_WIDTH + (row.length - 1) * GAP_X)) + 2 * PADDING;
  const x = new Map<string, number>();
  for (const row of rows) {
    const rowWidth = row.length * FAMILY_NODE_WIDTH + (row.length - 1) * GAP_X;
    row.forEach((member, i) => x.set(member._id, (width - rowWidth) / 2 + i * (FAMILY_NODE_WIDTH + GAP_X)));
  }
  const centerX = (id: string) => (x.get(id) ?? 0) + FAMILY_NODE_WIDTH / 2;

  const gaps = rows.slice(1).map(lowerRow => {
    const buses: Bus[] = [];
    for (const spender of [false, true]) {
      const byParents = new Map<string, Bus>();
      for (const child of lowerRow) {
        const parents = links
          .filter(link => link.spender === spender && link.to === child._id)
          .map(link => link.from)
          .sort((a, b) => (index.get(a) ?? 0) - (index.get(b) ?? 0));
        if (!parents.length) continue;
        const bus: Bus = byParents.get(parents.join()) ?? {
          spender,
          parents,
          children: [],
          parentX: new Map(),
          childX: new Map(),
          minX: 0,
          maxX: 0,
        };
        bus.children.push(child._id);
        byParents.set(parents.join(), bus);
      }
      buses.push(...byParents.values());
    }

    const spread = (center: number, i: number, count: number) => center + (i - (count - 1) / 2) * PORT_SPACING;
    // a parent in several units gets one port per unit, ordered towards the unit's children
    for (const parentID of new Set(buses.flatMap(bus => bus.parents))) {
      const mine = buses
        .filter(bus => bus.parents.includes(parentID))
        .sort((a, b) => average(a.children.map(centerX)) - average(b.children.map(centerX)));
      mine.forEach((bus, i) => bus.parentX.set(parentID, spread(centerX(parentID), i, mine.length)));
    }
    // a child gets one port per link, ordered like the parent ports they come from
    for (const childID of new Set(buses.flatMap(bus => bus.children))) {
      const incoming = buses
        .filter(bus => bus.children.includes(childID))
        .flatMap(bus => bus.parents.map(parentID => ({ bus, parentID, from: bus.parentX.get(parentID) ?? 0 })))
        .sort((a, b) => a.from - b.from);
      incoming.forEach(({ bus, parentID }, i) =>
        bus.childX.set(linkKey(parentID, childID), spread(centerX(childID), i, incoming.length)),
      );
    }
    for (const bus of buses) {
      const xs = [...bus.parentX.values(), ...bus.childX.values()];
      bus.minX = Math.min(...xs);
      bus.maxX = Math.max(...xs);
    }

    const lanes =
      buses.length > MAX_PERMUTED_BUSES
        ? buses
        : permutations(buses).reduce(
            (best, order) => (crossingCount(order) < crossingCount(best) ? order : best),
            buses,
          );
    return { lanes, height: Math.max(MIN_GAP_Y, (buses.length + 1) * LANE_SPACING) };
  });

  const rowY = [PADDING];
  for (const gap of gaps) rowY.push((rowY.at(-1) ?? PADDING) + FAMILY_NODE_HEIGHT + gap.height);
  const height = (rowY.at(-1) ?? PADDING) + FAMILY_NODE_HEIGHT + PADDING;

  const routed = gaps.flatMap((gap, i) => {
    const top = (rowY[i] ?? 0) + FAMILY_NODE_HEIGHT;
    const bottom = rowY[i + 1] ?? top;
    return gap.lanes.map((bus, lane) => ({
      bus,
      top,
      bottom,
      y: Math.round(top + ((bottom - top) * (lane + 1)) / (gap.lanes.length + 1)),
    }));
  });
  const lines = routed
    .map(({ bus, top, bottom, y }) => ({
      spender: bus.spender,
      touchesSelf: bus.parents.includes(selfID) || bus.children.includes(selfID),
      path: [
        `M ${bus.minX} ${y} H ${bus.maxX}`,
        ...[...bus.parentX.values()].map(px => `M ${px} ${top} V ${y}`),
        ...[...bus.childX.values()].map(cx => `M ${cx} ${y} V ${bottom}`),
      ].join(' '),
    }))
    // the person's own lines on top
    .sort((a, b) => Number(a.touchesSelf) - Number(b.touchesSelf));
  const graphLinks = routed.flatMap(({ bus, top, bottom, y }) =>
    bus.parents.flatMap(parentID =>
      bus.children.map(childID => ({
        parentID,
        childID,
        spender: bus.spender,
        path: `M ${bus.parentX.get(parentID) ?? 0} ${top} V ${y} H ${bus.childX.get(linkKey(parentID, childID)) ?? 0} V ${bottom}`,
      })),
    ),
  );

  const relationOf = (member: T): FamilyRelation => {
    if (member._id === selfID) return 'self';
    if (linked(member._id, selfID, false)) return 'sorgeberechtigt';
    if (linked(member._id, selfID, true)) return 'spender';
    if (linked(selfID, member._id, false)) return 'kind';
    if (linked(selfID, member._id, true)) return 'spendenkind';
    const selfParentIDs = sbParentIDs(self);
    const selfChildIDs = sbChildIDs(self);
    if (selfParentIDs.some(parentID => linked(member._id, parentID, false))) return 'grosselternteil';
    if (selfChildIDs.some(childID => linked(childID, member._id, false))) return 'enkelkind';
    if (sbParentIDs(member).some(parentID => selfParentIDs.includes(parentID))) return 'geschwister';
    if (selfChildIDs.some(childID => linked(member._id, childID, false))) return 'weitereSorgeberechtigte';
    return 'weitere';
  };

  return {
    width,
    height,
    lines,
    links: graphLinks,
    nodes: rows.flatMap((row, i) =>
      row.map(member => ({ member, relation: relationOf(member), x: x.get(member._id) ?? 0, y: rowY[i] ?? 0 })),
    ),
  };
}
